import * as CryptoJS from 'crypto-js'
import axios from 'axios'
import { checkFileMD5, initMultipartFile, mergeFileByMD5 } from '@/api/obs/obs'

/** 纯净 axios，用于预签名 URL 直传，避免携带业务 Token / Cookie */
const pureAxios = axios.create({
  withCredentials: false
})

/** check-md5 返回的文件状态 */
export const OBS_RESULT_TYPE = {
  FILE_EXISTS: 'file_exists',
  RESUME_UPLOAD: 'resume_upload',
  FILE_NOT_EXISTS: 'file_not_exists'
}

export const STATUS_TEXT = {
  CALCULATING_MD5: '正在计算文件校验码(MD5)...',
  CHECKING_STATUS: '正在检查文件状态...',
  INIT_MULTIPART: '正在初始化分片上传...',
  UPLOADING_CHUNKS: '正在上传分片...',
  RESUMING_UPLOAD: '正在断点续传...',
  MERGING_CHUNKS: '正在合并分片...',
  SUCCESS: '上传成功',
  FILE_EXIST: '文件已存在（秒传成功）'
}

const DEFAULT_CHUNK_SIZE = 2 * 1024 * 1024
const DEFAULT_MD5_CHUNK_SIZE = 2 * 1024 * 1024

/**
 * 规范化回调参数，兼容旧写法：
 * uploadFileToOBS(file, options, onProgress, onStatus, signal)
 * 以及新写法：
 * uploadFileToOBS(file, options, { onProgress, onMd5Progress, onStatus, signal })
 */
function normalizeCallbacks(onProgressOrCallbacks, onStatus, signal) {
  if (onProgressOrCallbacks && typeof onProgressOrCallbacks === 'object') {
    return onProgressOrCallbacks
  }
  return {
    onProgress: typeof onProgressOrCallbacks === 'function' ? onProgressOrCallbacks : undefined,
    onStatus: typeof onStatus === 'function' ? onStatus : undefined,
    signal
  }
}

function assertNotAborted(signal) {
  if (signal && signal.aborted) {
    throw new Error('上传已取消')
  }
}

/** 规范化 ETag，去掉多余引号 */
function normalizeEtag(etag) {
  if (!etag) return ''
  return String(etag).replace(/^"+|"+$/g, '').replace(/\\"/g, '')
}

/** 规范化分片 ETag 列表，并按 partNumber 去重排序 */
function normalizePartETags(partETags = []) {
  const map = new Map()
  partETags.forEach((item) => {
    if (!item || item.partNumber == null) return
    map.set(Number(item.partNumber), {
      partNumber: Number(item.partNumber),
      etag: normalizeEtag(item.etag)
    })
  })
  return Array.from(map.values()).sort((a, b) => a.partNumber - b.partNumber)
}

function getUrlMapKeys(urlMap = {}) {
  return Object.keys(urlMap)
    .map(Number)
    .filter((n) => !Number.isNaN(n))
    .sort((a, b) => a - b)
}

/**
 * 断点续传是否已全部上传完成（仅需合并）
 * @param {Object} resumeInfo
 * @param {number} fileSize
 * @param {number} chunkSize
 */
function isResumeUploadComplete(resumeInfo, fileSize, chunkSize) {
  if (!resumeInfo) return false

  const progressPercentage = Number(resumeInfo.progressPercentage)
  if (progressPercentage >= 100) return true

  const uploadedBytes = Number(resumeInfo.uploadedBytes)
  const totalSize = Number(resumeInfo.totalSize || fileSize)
  if (uploadedBytes > 0 && totalSize > 0 && uploadedBytes >= totalSize) return true

  const chunkCount = Number(resumeInfo.chunkCount) || Math.ceil(fileSize / chunkSize) || 0
  const completedCount =
    Number(resumeInfo.completedChunkCount) ||
    (Array.isArray(resumeInfo.completedPartETags) ? resumeInfo.completedPartETags.length : 0)

  if (chunkCount > 0 && completedCount >= chunkCount) return true

  // urlMap 为空且已有完成分片，也视为可直接合并
  const pendingUrls = getUrlMapKeys(resumeInfo.urlMap)
  if (!pendingUrls.length && completedCount > 0 && chunkCount > 0 && completedCount >= chunkCount) {
    return true
  }

  return false
}

/**
 * 解析 check-md5 的三种状态，得到统一上传上下文
 *
 * @returns {Promise<{
 *   mode: 'instant' | 'resume' | 'fresh',
 *   fileInfo?: Object,
 *   uploadId?: string,
 *   urlMap?: Object,
 *   completedPartETags?: Array,
 *   chunkSize?: number,
 *   skipChunkUpload?: boolean
 * }>}
 */
async function resolveUploadContext(file, md5, obsEnums, chunkSize, emitStatus) {
  const checkData = await checkFileStatus({
    md5,
    originFileName: file.name,
    obsEnums
  })

  const resultType = checkData.resultType
  const needInitUpload = !!checkData.needInitUpload

  // 1) 秒传
  if (resultType === OBS_RESULT_TYPE.FILE_EXISTS || resultType === 1) {
    if (!checkData.fileInfo) {
      throw new Error('文件已存在，但未返回文件信息')
    }
    return {
      mode: 'instant',
      fileInfo: checkData.fileInfo
    }
  }

  // 2) 断点续传
  if (resultType === OBS_RESULT_TYPE.RESUME_UPLOAD || resultType === 2) {
    const resumeInfo = checkData.resumeInfo || {}
    const uploadId = resumeInfo.uploadId
    const urlMap = resumeInfo.urlMap || {}
    const completedPartETags = normalizePartETags(resumeInfo.completedPartETags || [])
    const resumeChunkSize = Number(resumeInfo.chunkSize) || chunkSize
    const hasPendingUrls = getUrlMapKeys(urlMap).length > 0
    const resumeComplete = isResumeUploadComplete(resumeInfo, file.size, resumeChunkSize)

    // 分片已全部传完：只合并，不需要 urlMap
    if (uploadId && resumeComplete) {
      return {
        mode: 'resume',
        uploadId,
        urlMap: {},
        completedPartETags,
        chunkSize: resumeChunkSize,
        skipChunkUpload: true
      }
    }

    // 还有待传分片地址：断点续传
    if (uploadId && hasPendingUrls && !needInitUpload) {
      return {
        mode: 'resume',
        uploadId,
        urlMap,
        completedPartETags,
        chunkSize: resumeChunkSize,
        skipChunkUpload: false
      }
    }

    // 续传信息不完整 / 需要重新初始化：走全新初始化
    // （例如 uploadId 缺失、urlMap 为空且未完成、needInitUpload=true）
  }

  // 3) 文件未上传，或其他需重新初始化的情况
  if (
    resultType !== OBS_RESULT_TYPE.FILE_NOT_EXISTS &&
    resultType !== 3 &&
    resultType !== OBS_RESULT_TYPE.RESUME_UPLOAD &&
    resultType !== 2
  ) {
    console.warn('checkFileStatus unknown resultType, fallback to init:', resultType)
  }

  emitStatus?.(STATUS_TEXT.INIT_MULTIPART)
  const initData = await initFileChunksUpload(file, md5, chunkSize, obsEnums)
  if (!initData || !initData.uploadId || !initData.urlMap || !getUrlMapKeys(initData.urlMap).length) {
    throw new Error('初始化分片上传失败，未获取到上传凭证')
  }

  return {
    mode: 'fresh',
    uploadId: initData.uploadId,
    urlMap: initData.urlMap,
    completedPartETags: [],
    chunkSize,
    skipChunkUpload: false
  }
}

/**
 * ArrayBuffer → CryptoJS WordArray
 */
function arrayBufferToWordArray(arrayBuffer) {
  const u8 = new Uint8Array(arrayBuffer)
  const len = u8.length
  const words = []
  for (let i = 0; i < len; i += 1) {
    words[i >>> 2] |= u8[i] << (24 - (i % 4) * 8)
  }
  return CryptoJS.lib.WordArray.create(words, len)
}

/**
 * 上传文件到 OBS（通用入口）
 *
 * 流程：
 * 1. 计算 MD5
 * 2. check-md5 判断状态
 *    - file_exists：秒传，直接返回 fileInfo
 *    - resume_upload：
 *        · 分片已全部完成 → 直接合并
 *        · 仍有待传 urlMap → 断点续传后合并
 *        · 信息不完整 / needInitUpload → 重新初始化后上传
 *    - file_not_exists：初始化分片后上传
 * 3. 分片 PUT 到预签名 URL
 * 4. 合并分片
 *
 * @param {File} file
 * @param {Object} [options]
 * @param {string} [options.obsEnums='OTA'] - OBS 业务枚举：OTA / LOG 等
 * @param {number} [options.chunkSize=2097152] - 上传分片大小，默认 2MB
 * @param {number} [options.md5ChunkSize=2097152] - MD5 分片大小，默认 2MB
 * @param {Function|Object} [onProgressOrCallbacks] - 进度回调，或回调对象
 * @param {Function} [onProgressOrCallbacks.onProgress] - 上传进度 0~100
 * @param {Function} [onProgressOrCallbacks.onMd5Progress] - MD5 进度 0~100
 * @param {Function} [onProgressOrCallbacks.onStatus] - 状态文案回调
 * @param {AbortSignal} [onProgressOrCallbacks.signal] - 取消信号
 * @param {Function} [onStatus] - 兼容旧参数：状态回调
 * @param {AbortSignal} [signal] - 兼容旧参数：取消信号
 * @returns {Promise<Object>} 上传成功后的文件信息
 */
export async function uploadFileToOBS(file, options = {}, onProgressOrCallbacks, onStatus, signal) {
  if (!file) {
    throw new Error('请选择要上传的文件')
  }

  const chunkSize = options.chunkSize || DEFAULT_CHUNK_SIZE
  const md5ChunkSize = options.md5ChunkSize || DEFAULT_MD5_CHUNK_SIZE
  const obsEnums = options.obsEnums || 'OTA'
  const callbacks = normalizeCallbacks(onProgressOrCallbacks, onStatus, signal)
  const {
    onProgress,
    onMd5Progress,
    onStatus: emitStatus,
    signal: abortSignal
  } = callbacks

  assertNotAborted(abortSignal)

  // 1. 计算 MD5（总进度 0% ~ 15%）
  emitStatus?.(STATUS_TEXT.CALCULATING_MD5)
  const md5 = await calculateFileMD5(
    file,
    (md5Percent) => {
      onMd5Progress?.(md5Percent)
      if (!onMd5Progress) {
        onProgress?.(Math.floor(md5Percent * 0.15))
      }
    },
    null,
    md5ChunkSize,
    abortSignal
  )

  assertNotAborted(abortSignal)

  // 2. 检查文件状态并解析上传上下文
  emitStatus?.(STATUS_TEXT.CHECKING_STATUS)
  const context = await resolveUploadContext(file, md5, obsEnums, chunkSize, emitStatus)

  // 2.1 秒传
  if (context.mode === 'instant') {
    emitStatus?.(STATUS_TEXT.FILE_EXIST)
    onMd5Progress?.(100)
    onProgress?.(100)
    return context.fileInfo
  }

  const {
    uploadId,
    urlMap,
    completedPartETags,
    chunkSize: effectiveChunkSize,
    skipChunkUpload
  } = context

  if (skipChunkUpload) {
    emitStatus?.(STATUS_TEXT.MERGING_CHUNKS)
    onProgress?.(onMd5Progress ? 100 : 90)
  } else if (context.mode === 'resume') {
    emitStatus?.(STATUS_TEXT.RESUMING_UPLOAD)
  }

  assertNotAborted(abortSignal)

  // 3. 分片上传（续传只传 urlMap 中待传分片；已全部完成则跳过）
  let newPartETags = []
  if (!skipChunkUpload) {
    emitStatus?.(STATUS_TEXT.UPLOADING_CHUNKS)
    newPartETags = await uploadRealFileChunks(
      file,
      urlMap,
      effectiveChunkSize,
      (uploadPercent) => {
        if (onMd5Progress) {
          onProgress?.(uploadPercent)
        } else {
          const totalPercent = 15 + Math.floor(uploadPercent * 0.75)
          onProgress?.(totalPercent > 90 ? 90 : totalPercent)
        }
      },
      abortSignal,
      {
        completedPartNumbers: completedPartETags.map((item) => item.partNumber)
      }
    )
  }

  assertNotAborted(abortSignal)

  // 4. 合并分片（已完成 + 新上传，去重排序）
  emitStatus?.(STATUS_TEXT.MERGING_CHUNKS)
  const partETags = normalizePartETags([...completedPartETags, ...newPartETags])
  if (!partETags.length) {
    throw new Error('合并分片失败：缺少分片校验信息')
  }

  const mergeRes = await mergeFileChunks({
    uploadId,
    partETags,
    md5,
    originFileName: file.name,
    obsEnums
  })

  emitStatus?.(STATUS_TEXT.SUCCESS)
  onProgress?.(100)

  return mergeRes.data?.data || mergeRes.data
}

/**
 * 分片计算文件 MD5（支持大文件 / 可取消）
 *
 * @param {File} file
 * @param {Function} [onProgress] - 0~100
 * @param {Function} [onStatusChange] - true=计算中，false=结束
 * @param {number} [chunkSize=2MB]
 * @param {AbortSignal} [signal]
 * @returns {Promise<string>}
 */
export function calculateFileMD5(
  file,
  onProgress,
  onStatusChange,
  chunkSize = DEFAULT_MD5_CHUNK_SIZE,
  signal
) {
  return new Promise((resolve, reject) => {
    if (signal && signal.aborted) {
      reject(new Error('上传已取消'))
      return
    }

    if (typeof onStatusChange === 'function') onStatusChange(true)
    if (typeof onProgress === 'function') onProgress(0)

    const blobSlice = File.prototype.slice || File.prototype.mozSlice || File.prototype.webkitSlice
    const chunks = Math.max(1, Math.ceil(file.size / chunkSize))
    let currentChunk = 0
    let settled = false
    const md5Hash = CryptoJS.algo.MD5.create()
    const fileReader = new FileReader()

    const fail = (error) => {
      if (settled) return
      settled = true
      try {
        if (fileReader.readyState === FileReader.LOADING) {
          fileReader.abort()
        }
      } catch (e) {
        // ignore
      }
      if (typeof onStatusChange === 'function') onStatusChange(false)
      reject(error)
    }

    const onAbort = () => {
      fail(new Error('上传已取消'))
    }

    if (signal) {
      signal.addEventListener('abort', onAbort, { once: true })
    }

    const loadNext = () => {
      if (settled) return
      if (signal && signal.aborted) {
        fail(new Error('上传已取消'))
        return
      }

      const start = currentChunk * chunkSize
      const end = Math.min(start + chunkSize, file.size)
      fileReader.readAsArrayBuffer(blobSlice.call(file, start, end))
    }

    fileReader.onload = (e) => {
      try {
        if (settled) return
        if (signal && signal.aborted) {
          fail(new Error('上传已取消'))
          return
        }

        md5Hash.update(arrayBufferToWordArray(e.target.result))
        currentChunk += 1

        const progress = Math.floor((currentChunk / chunks) * 100)
        if (typeof onProgress === 'function') onProgress(progress)

        if (currentChunk < chunks) {
          loadNext()
        } else {
          if (settled) return
          settled = true
          if (signal) {
            signal.removeEventListener('abort', onAbort)
          }
          const md5 = md5Hash.finalize().toString()
          if (typeof onStatusChange === 'function') onStatusChange(false)
          if (typeof onProgress === 'function') onProgress(100)
          resolve(md5)
        }
      } catch (error) {
        fail(error)
      }
    }

    fileReader.onerror = () => {
      fail(new Error('文件读取失败'))
    }

    fileReader.onabort = () => {
      fail(new Error('上传已取消'))
    }

    loadNext()
  })
}

/**
 * 检查文件状态（秒传 / 断点续传 / 未上传）
 *
 * @param {{ md5: string, originFileName: string, obsEnums: string }} options
 * @param {Object} [callbacks]
 * @param {Function} [callbacks.onFileExist]
 * @param {Function} [callbacks.onResumeUpload]
 * @param {Function} [callbacks.onFileNotExist]
 * @returns {Promise<Object>} check-md5 的 data 字段
 */
export async function checkFileStatus(options, callbacks = {}) {
  const { onFileExist, onResumeUpload, onFileNotExist } = callbacks
  const checkResult = await checkFileMD5(options)
  const data = checkResult.data?.data || {}
  const resultType = data.resultType

  switch (resultType) {
    case OBS_RESULT_TYPE.FILE_EXISTS:
    case 1:
      onFileExist?.(data)
      break
    case OBS_RESULT_TYPE.RESUME_UPLOAD:
    case 2:
      onResumeUpload?.(data)
      break
    case OBS_RESULT_TYPE.FILE_NOT_EXISTS:
    case 3:
      onFileNotExist?.(data)
      break
    default:
      console.warn('checkFileStatus unknown resultType:', resultType)
  }

  return data
}

/**
 * 初始化分片上传，返回 uploadId 与各分片预签名 URL
 *
 * @param {File} file
 * @param {string} md5
 * @param {number} [chunkSize=2MB]
 * @param {string} [obsEnums='OTA']
 * @returns {Promise<{ uploadId: string, urlMap: Object }>}
 */
export async function initFileChunksUpload(file, md5, chunkSize = DEFAULT_CHUNK_SIZE, obsEnums = 'OTA') {
  const chunkCount = Math.max(1, Math.ceil(file.size / chunkSize))
  const res = await initMultipartFile({
    md5,
    originFileName: file.name,
    size: file.size,
    obsEnums,
    chunkCount,
    chunkSize
  })
  return res.data?.data
}

/**
 * 将文件分片 PUT 到 OBS 预签名地址
 *
 * 优先按 urlMap 中的分片号上传（断点续传时 urlMap 通常只含未完成分片）；
 * 若 urlMap 为空则按文件总分片顺序上传。
 *
 * @param {File} file
 * @param {Object} urlMap - { 1: url, 2: url, ... }
 * @param {number} [chunkSize=2MB]
 * @param {Function} [onProgress] - 0~100（相对整个文件）
 * @param {AbortSignal} [signal]
 * @param {Object} [options]
 * @param {number[]} [options.completedPartNumbers=[]] - 已完成分片号，续传时跳过
 * @returns {Promise<Array<{ partNumber: number, etag: string }>>} 本次新上传的分片 ETag 列表
 */
export async function uploadRealFileChunks(
  file,
  urlMap,
  chunkSize = DEFAULT_CHUNK_SIZE,
  onProgress,
  signal,
  options = {}
) {
  const totalSize = file.size
  const totalChunks = Math.max(1, Math.ceil(totalSize / chunkSize))
  const completedSet = new Set((options.completedPartNumbers || []).map(Number))
  const blobSlice = File.prototype.slice || File.prototype.mozSlice || File.prototype.webkitSlice
  const partETags = []

  // 待上传分片：优先使用 urlMap 的 key；否则按总分片生成
  let pendingPartNumbers = getUrlMapKeys(urlMap).filter((partNumber) => !completedSet.has(partNumber))
  if (!pendingPartNumbers.length) {
    pendingPartNumbers = []
    for (let i = 1; i <= totalChunks; i += 1) {
      if (!completedSet.has(i)) {
        pendingPartNumbers.push(i)
      }
    }
  }

  if (!pendingPartNumbers.length) {
    onProgress?.(100)
    return []
  }

  const completedBytes = Math.min(completedSet.size * chunkSize, totalSize)
  let uploadedBytes = completedBytes
  const basePercent = Math.floor((completedBytes / totalSize) * 100)
  onProgress?.(Math.max(basePercent, 1))

  for (let i = 0; i < pendingPartNumbers.length; i += 1) {
    assertNotAborted(signal)

    const partNumber = pendingPartNumbers[i]
    const url = urlMap[partNumber] || urlMap[String(partNumber)]
    if (!url) {
      throw new Error(`缺少分片 ${partNumber} 的上传地址`)
    }

    const start = (partNumber - 1) * chunkSize
    const end = Math.min(start + chunkSize, totalSize)
    const chunkBlob = blobSlice.call(file, start, end)

    const res = await pureAxios.put(url, chunkBlob, {
      headers: {
        'Content-Type': 'application/octet-stream'
      },
      withCredentials: false,
      signal,
      onUploadProgress: (progressEvent) => {
        const currentTotal = uploadedBytes + (progressEvent.loaded || 0)
        const percent = Math.floor((currentTotal / totalSize) * 100)
        onProgress?.(percent > 100 ? 100 : Math.max(percent, 1))
      }
    })

    uploadedBytes += chunkBlob.size

    let etag = res.headers.etag || res.headers.ETag || ''
    etag = normalizeEtag(etag)

    partETags.push({
      partNumber,
      etag
    })

    const currentProgress = Math.floor((uploadedBytes / totalSize) * 100)
    onProgress?.(currentProgress === 0 ? 1 : Math.min(currentProgress, 100))
  }

  return partETags
}

/**
 * 校验并合并文件分片
 * @param {Object} params - { uploadId, partETags, md5?, originFileName?, obsEnums? }
 * @returns {Promise<Object>}
 */
export async function mergeFileChunks(params) {
  return mergeFileByMD5({
    ...params,
    partETags: normalizePartETags(params.partETags || [])
  })
}
